'use client'

import {
	FileUpload as FileUploadPrimitive,
	useFileUpload,
	UseFileUploadReturn,
} from '@ark-ui/react/file-upload'
import {
	ComponentProps,
	forwardRef,
	ReactElement,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'

import { AllOrNone, cn } from '@/utils'

import { fieldErrorCls } from '../InputField'
import { ImageCropDialog } from './DialogImageCrop/ImageCropDialog'
import { Dropzone } from './Dropzone'
import { getImageFileResolution } from './getImageFileResolution'
import { MultiFilePreview } from './MultiFilePreview'
import { SingleFilePreview } from './SingleFilePreview'

export type TResolutionLimitProps = {
	imageResolutionLimit: { width: number; height: number }
	resolutionTooLargeErrorMessage: string
}

export type TFileUploadProps = FileUploadPrimitive.RootProps & {
	files: File[]
	onFileUpload: (file: File) => void
	onFileReplace: (file: File, index: number) => void
	onFileDelete: (file: File, index: number) => void
	progress: Record<string, number>
	maxFiles?: number
	onMaxFilesExceeded?: (maxFiles: number) => void
	placeholderIcon?: ReactElement
	showPreviewButton?: boolean
	onClickShowPreview?: () => void
	showCropDialog?: boolean
	cropDialogTranslations?: ComponentProps<
		typeof ImageCropDialog
	>['translations']
	fileNameTransformer?: (name: string) => string
	getFileKey?: (file: File, index: number) => string
	translations: {
		title: string
		caption: string
		deleteButtonLabel?: string
		unsupportedFormatErrorMessage: string | ((errors: string[]) => void)
		sizeTooLargeErrorMessage: string
		maxFilesLimitError?: string | ((count: number) => void)
	}
	hideSingleFilePreview?: boolean
	hideMultiFilePreview?: boolean
	aspect?: number
	hideDropZone?: boolean
	maintainAspectRatio?: boolean
} & AllOrNone<TResolutionLimitProps>

export type TFileUploadHandle = {
	setError: (message: string) => void
	clearError: () => void
}

const DEFAULT_ACCEPT = ['image/jpeg', 'image/png', 'image/svg+xml']

export const FileUpload = forwardRef<TFileUploadHandle, TFileUploadProps>(
	(
		{
			files,
			onFileUpload,
			onFileReplace,
			onFileDelete,
			progress,
			maxFiles = 1,
			onMaxFilesExceeded,
			maxFileSize,
			placeholderIcon,
			showPreviewButton,
			onClickShowPreview,
			imageResolutionLimit,
			resolutionTooLargeErrorMessage,
			showCropDialog,
			cropDialogTranslations,
			fileNameTransformer,
			getFileKey,
			translations: {
				title,
				caption,
				deleteButtonLabel,
				unsupportedFormatErrorMessage,
				sizeTooLargeErrorMessage,
				maxFilesLimitError,
			},
			className,
			accept = DEFAULT_ACCEPT,
			hideSingleFilePreview,
			hideMultiFilePreview,
			disabled,
			aspect,
			hideDropZone,
			maintainAspectRatio,
			...restProps
		},
		ref,
	) => {
		const isMultiFileMode = maxFiles > 1
		const resolveFileKey = useCallback(
			(file: File, index: number) =>
				getFileKey?.(file, index) || file.name,
			[getFileKey],
		)

		const [error, setError] = useState('')
		const [isCropDialogOpen, setIsCropDialogOpen] = useState(false)
		const [pendingFile, setPendingFile] = useState<File | null>(null)
		const [fileToReplaceIndex, setFileToReplaceIndex] = useState<
			number | null
		>(null)
		const [cropDialogKey, setCropDialogKey] = useState(0)
		const [singleFilePreviewKey, setSingleFilePreviewKey] = useState(0)

		useImperativeHandle(
			ref,
			() => ({
				setError: (message) => {
					setError(message)
				},
				clearError: () => {
					setError('')
				},
			}),
			[],
		)

		const handleValidation = useCallback(
			async (file: File): Promise<string | null> => {
				if (maxFileSize && file.size > maxFileSize)
					return sizeTooLargeErrorMessage
				if (imageResolutionLimit) {
					const { width, height } = await getImageFileResolution(file)
					if (
						width > imageResolutionLimit.width ||
						height > imageResolutionLimit.height
					) {
						return resolutionTooLargeErrorMessage
					}
				}
				return null
			},
			[
				imageResolutionLimit,
				maxFileSize,
				resolutionTooLargeErrorMessage,
				sizeTooLargeErrorMessage,
			],
		)

		const handleSave = useCallback(
			async (newFiles: File[]) => {
				setSingleFilePreviewKey((prev) => prev + 1)
				setIsCropDialogOpen(false)

				for (const file of newFiles) {
					const validationError = await handleValidation(file)
					if (validationError) {
						setError(validationError)
						continue
					}
					if (fileToReplaceIndex !== null) {
						onFileReplace(file, fileToReplaceIndex)
						setFileToReplaceIndex(null)
					} else {
						onFileUpload(file)
					}
				}
			},
			[fileToReplaceIndex, handleValidation, onFileReplace, onFileUpload],
		)

		const clearFilesRef = useRef<
			UseFileUploadReturn['clearFiles'] | undefined
		>(undefined)

		const handleFileAccept = useCallback<
			NonNullable<TFileUploadProps['onFileAccept']>
		>(
			({ files: acceptedFiles }) => {
				if (acceptedFiles.length === 0) {
					return
				}
				const totalFileCount =
					files.length -
					// If replacing a file then subtract one since the total count will not change
					(fileToReplaceIndex === null ? 0 : 1) +
					acceptedFiles.length
				if (totalFileCount > maxFiles) {
					acceptedFiles.splice(maxFiles - files.length)
					onMaxFilesExceeded?.(maxFiles)
				} else {
					setError('')
				}
				clearFilesRef.current?.()
				const uploadedFile = acceptedFiles[acceptedFiles.length - 1]
				if (!uploadedFile) {
					return
				}

				if (showCropDialog) {
					setPendingFile(uploadedFile)
					setIsCropDialogOpen(true)
					setCropDialogKey((prev) => prev + 1)
				} else {
					handleSave(acceptedFiles)
				}
			},
			[
				fileToReplaceIndex,
				files,
				handleSave,
				maxFiles,
				onMaxFilesExceeded,
				showCropDialog,
			],
		)

		const api = useFileUpload({
			// Tp prevent the upload library from throwing error without uploading any file if more than 10 files are selected at once, we need to pass infinity to bypass internal validation.
			// This allows our code to handle file count limit and upload files until limit reached.
			maxFiles: maxFiles === 1 || fileToReplaceIndex ? 1 : Infinity,
			accept,
			onFileAccept: handleFileAccept,
			onFileReject: (rejectDetails) => {
				const { files } = rejectDetails
				if (files.length > maxFiles) {
					if (typeof maxFilesLimitError === 'function') {
						maxFilesLimitError(maxFiles)
					} else {
						setError(maxFilesLimitError || '')
					}
				} else {
					if (typeof unsupportedFormatErrorMessage === 'function') {
						const errors = files.map(
							(file) => file.errors[0] as string,
						)
						unsupportedFormatErrorMessage(errors)
					} else {
						setError(unsupportedFormatErrorMessage)
					}
				}
			},
			...restProps,
		})

		useEffect(() => {
			clearFilesRef.current = api.clearFiles
		}, [api])

		const handleReplace = (fileIndex: number) => {
			setFileToReplaceIndex(fileIndex)
			api.openFilePicker()
		}

		const showDropzoneContainer =
			(!isMultiFileMode ||
				(isMultiFileMode && files.length < maxFiles)) &&
			!hideDropZone

		const showDropZone =
			isMultiFileMode || (!isMultiFileMode && files.length === 0)

		const showSingleFilePreview = !isMultiFileMode && !!files[0]

		const showMultiFilePreview =
			isMultiFileMode && !!files.length && !hideMultiFilePreview

		return (
			<FileUploadPrimitive.RootProvider value={api} className={className}>
				{showCropDialog && cropDialogTranslations && pendingFile && (
					<ImageCropDialog
						key={`${cropDialogKey}`}
						isOpen={isCropDialogOpen}
						setIsOpen={setIsCropDialogOpen}
						originalFile={pendingFile}
						onSave={(file) => handleSave([file])}
						translations={cropDialogTranslations}
						aspect={aspect}
					/>
				)}

				{showDropzoneContainer && showDropZone ? (
					<FileUploadPrimitive.Dropzone>
						<FileUploadPrimitive.Trigger
							disabled={disabled}
							className={cn(
								'w-full rounded-10 border-[1.5px] border-dashed border-blue p-[calc(theme(spacing.4)_-_1.5px)] transition-colors tablet:gap-6 tablet:p-[calc(theme(spacing.6)_-_1.5px)] [&:not(:has([role=button]:hover))]:hover:bg-light-blue',
								error && 'border-error',
								disabled && 'cursor-not-allowed border-grey',
							)}
						>
							<Dropzone
								title={title}
								description={caption}
								placeholderIcon={placeholderIcon}
							/>
						</FileUploadPrimitive.Trigger>
					</FileUploadPrimitive.Dropzone>
				) : null}

				{showSingleFilePreview ? (
					<div
						className={cn(
							'w-full cursor-auto rounded-10 border-[1.5px] border-dashed border-grey p-[calc(theme(spacing.4)_-_1.5px)] transition-colors tablet:gap-6 tablet:p-[calc(theme(spacing.6)_-_1.5px)] [&:not(:has([role=button]:hover))]:hover:bg-light-blue',
							error && 'border-error',
						)}
					>
						<FileUploadPrimitive.ItemGroup>
							<SingleFilePreview
								key={`${singleFilePreviewKey}-${resolveFileKey(files[0]!, 0)}`}
								file={files[0]!}
								onReplace={() => handleReplace(0)}
								onDelete={onFileDelete}
								deleteButtonLabel={deleteButtonLabel}
								showPreviewButton={showPreviewButton}
								onPreview={onClickShowPreview}
								progress={
									progress?.[resolveFileKey(files[0]!, 0)] ||
									0
								}
								hidePreview={hideSingleFilePreview}
								placeholderIcon={placeholderIcon}
								fileNameTransformer={
									fileNameTransformer || null
								}
								maintainAspectRatio={maintainAspectRatio}
							/>
						</FileUploadPrimitive.ItemGroup>
					</div>
				) : null}

				{error ? (
					<p className={cn(fieldErrorCls, 'mt-2')}>{error}</p>
				) : null}

				{showMultiFilePreview ? (
					<FileUploadPrimitive.ItemGroup>
						<MultiFilePreview
							files={files}
							onReplace={handleReplace}
							onDelete={onFileDelete}
							deleteButtonLabel={deleteButtonLabel}
							showPreviewButton={showPreviewButton}
							onPreview={onClickShowPreview}
							topMargin={showDropzoneContainer}
							progress={progress}
							getFileKey={resolveFileKey}
						/>
					</FileUploadPrimitive.ItemGroup>
				) : null}

				<FileUploadPrimitive.HiddenInput />
			</FileUploadPrimitive.RootProvider>
		)
	},
)

FileUpload.displayName = 'FileUpload'
